import { useEffect } from 'react';

import { useLatestRef } from '@heidi/utils';

import type { LinkedContextItem } from '../../contracts/projects/linked-context';
import type { ProjectsComposerPromptDraft } from './projects-composer-editor-draft';

/** A submitted turn's composer state — the restore candidate. */
export interface DiscardableTurn {
  readonly draft: ProjectsComposerPromptDraft;
  readonly files?: File[];
  /**
   * Linked session/patient context attached to the turn. Restored alongside the
   * draft so resending keeps the context preamble — without it a resent prompt
   * silently runs against no patient/session.
   */
  readonly linkedItems?: readonly LinkedContextItem[];
}

interface UseRestoreDiscardedDraftArgs {
  /**
   * Bumps each time the in-flight turn was cancelled before the backend
   * persisted anything for it (a pre-output stop the backend discards). The
   * restore runs on each change.
   */
  discardedTurnNonce: number | undefined;
  /**
   * The turn to restore, remembered at submit time by the transport (the single
   * choke point all project submits pass through — composer, seeded first turn,
   * and published submit alike). Null when nothing was submitted.
   */
  discardedTurn: DiscardableTurn | null;
  /**
   * Whether the composer is pristine (empty draft, no queued turns, no staged
   * files, no linked context). Restore is skipped unless true, so it never
   * clobbers anything the user has started building for their next message.
   */
  canRestore: boolean;
  /** Apply the recovered prompt + attachments + linked context to the composer. */
  onRestore: (turn: DiscardableTurn) => void;
}

/**
 * Returns the just-cancelled prompt to the composer when its turn was discarded
 * before the backend produced anything (the user bubble is dropped from the
 * transcript, so the typed text would otherwise be lost).
 *
 * Restores only into a pristine composer (`canRestore`), so it never clobbers a
 * reply, attachment, or linked context the user has started for their next
 * message. The turn is remembered by the transport at submit time and handed in
 * via `discardedTurn`; on each discard nonce it is replayed once.
 */
export const useRestoreDiscardedDraft = ({
  discardedTurnNonce,
  discardedTurn,
  canRestore,
  onRestore,
}: UseRestoreDiscardedDraftArgs): void => {
  // Latest-value refs so the discard effect reads the current turn / guard /
  // handler at fire time without re-running on every keystroke (it depends only
  // on the nonce).
  const discardedTurnRef = useLatestRef(discardedTurn);
  const canRestoreRef = useLatestRef(canRestore);
  const onRestoreRef = useLatestRef(onRestore);

  useEffect(() => {
    const turn = discardedTurnRef.current;
    if (!turn) return;
    if (!canRestoreRef.current) return;
    onRestoreRef.current(turn);
    // The refs are stable (latest-value), so listing them never re-fires the
    // effect — it still runs only when the discard nonce changes.
  }, [discardedTurnNonce, discardedTurnRef, canRestoreRef, onRestoreRef]);
};
